import { z } from 'zod';

// ============================================
// Extended Branding Section Schemas
// ============================================

/** Footer branding schema */
export const brandingFooterSchema = z.object({
  license: z.string().optional(),
});

/** Property details branding schema */
export const brandingPropertyDetailsSchema = z.object({
  contact: z.string().optional(),
  tour: z.string().optional(),
});

/** Squeeze page branding schema */
export const brandingSqueezePageSchema = z.object({
  language: z.string().optional(),
  // SPEC-2232: which consent treatment the IDX sign-up surfaces render --
  // 'passive' (no checkbox), 'bundled' (one blocking checkbox) or 'split' (two
  // optional checkboxes). Persisted to the dedicated
  // Organizations.idx_consent_style column, not the branding blob. Undefined
  // means "not set", leaving the org on the IDX site's allowlist fallback.
  consentStyle: z.enum(['passive', 'bundled', 'split']).optional(),
});

/** Primary contact schema with conditional validation */
export const primaryContactSchema = z
  .object({
    name: z.string().optional(),
    title: z.string().optional(),
    email: z.string().optional(),
    phone: z.string().optional(),
    useForPropertyPage: z.boolean().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.useForPropertyPage) {
      if (!data.name?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Contact Name is required when Primary Contact is enabled',
          path: ['name'],
        });
      }
      if (!data.email?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Email is required when Primary Contact is enabled',
          path: ['email'],
        });
      }
      if (!data.phone?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Phone is required when Primary Contact is enabled',
          path: ['phone'],
        });
      }
    }
  });

// ============================================
// Main Branding Schema
// ============================================

export const brandingSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  colors: z.object({
    primary: z.string(),
    secondary: z.string(),
  }),
  logo: z.object({
    url: z.string().optional(),
    placement: z.enum(['header', 'footer', 'both']).default('header').optional(),
  }),
  favicon: z
    .object({
      url: z.string().optional(),
    })
    .optional(),
  complianceLinks: z
    .array(
      z.object({
        title: z.string(),
        url: z.string().url('Invalid URL'),
      })
    )
    .optional(),
  // Extended branding sections
  teamName: z.string().optional(),
  brokerageName: z.string().optional(),
  footer: brandingFooterSchema.optional(),
  propertyDetails: brandingPropertyDetailsSchema.optional(),
  squeezePage: brandingSqueezePageSchema.optional(),
  primaryContact: primaryContactSchema.optional(),
});

export type BrandingFormData = z.infer<typeof brandingSchema>;
